import { createContext, useContext, useEffect, useRef, useState, type SVGProps } from 'react';

export const EagerArt = createContext(false);
/** Static face baking uses already checked/embedded URLs; it never starts lazy DOM artwork jobs. */
export const SnapshotArt = createContext(false);
const jobs = new Map<string, Promise<boolean>>();
const settled = new Set<string>();
const queue: (() => void)[] = [];
let active = 0;
function pump() { while (active < 4 && queue.length) { active++; queue.shift()!(); } }
/** Deduplicated bounded decoder. Retains result metadata, never a catalog of decoded Image objects. */
export function loadArtImage(url: string): Promise<boolean> {
  const hit = jobs.get(url); if (hit) return hit;
  if (queue.length >= 2048) return Promise.resolve(false);
  const promise = new Promise<boolean>(resolve => {
    queue.push(() => {
      const img = new Image(); let done = false;
      const finish = (ok: boolean) => {
        if (done) return; done = true; clearTimeout(timer); img.onload = null; img.onerror = null;
        if (!ok) { img.src = ''; jobs.delete(url); }
        else {
          settled.add(url);
          // Never evict in-flight jobs: large contact sheets must remain deduplicated.
          if (settled.size > 256) { const oldest = settled.values().next().value!; settled.delete(oldest); jobs.delete(oldest); }
        }
        active--; resolve(ok); pump();
      };
      const timer = setTimeout(() => finish(false), 15000);
      img.onload = () => { void img.decode().then(() => finish(true), () => finish(false)); };
      img.onerror = () => finish(false); img.src = url;
    });
  });
  jobs.set(url, promise);
  pump(); return promise;
}

/**
 * `keepPrevious` is for art whose registration is the same at every resolution (board layers): the last
 * decoded image stays on screen while a sharper one decodes or while it is scrolled out of view,
 * instead of blinking to the placeholder.
 */
export function SvgImage({ href, keepPrevious = false, placeholder = true, ...props }: SVGProps<SVGImageElement> & { href: string; keepPrevious?: boolean; placeholder?: boolean }) {
  const snapshot = useContext(SnapshotArt);
  const ref = useRef<SVGGElement>(null); const eager = useContext(EagerArt);
  const [visible, setVisible] = useState(eager);
  const [loaded, setLoaded] = useState<{ url: string; ok: boolean } | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const [painted, setPainted] = useState<string | null>(null);
  useEffect(() => { if (!visible && !keepPrevious) setPainted(null); }, [visible, keepPrevious]);
  useEffect(() => {
    if (eager || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    // Some WebKit versions do not report transformed SVG groups as intersecting.
    // Use the nearest cropped SVG viewport, not an atlas's source-sheet content group.
    // Recheck current clipped geometry as a compatibility path, without an idle poll.
    const target = ref.current?.ownerSVGElement ?? ref.current;
    let frame = 0, disposed = false, viewportReported = false, revealed = false;
    const reveal = (intersects: boolean) => {
      if (!intersects || revealed || disposed) return;
      revealed = true; setVisible(true);
      observer.disconnect(); transformed.disconnect(); cancelAnimationFrame(frame); frame = 0;
      window.removeEventListener('resize', schedule); document.removeEventListener('scroll', schedule, true);
    };
    const check = () => {
      frame = 0; const element = target; if (disposed || revealed || viewportReported || !element?.isConnected) return;
      let box = element.getBoundingClientRect();
      if (!box.width || !box.height) box = (element as SVGElement).ownerSVGElement?.getBoundingClientRect() ?? box;
      let left = -200, top = -200, right = innerWidth + 200, bottom = innerHeight + 200;
      for (let parent = element.parentElement; parent; parent = parent.parentElement) {
        const style = getComputedStyle(parent);
        if (style.display === 'none' || style.visibility === 'hidden') { return; }
        if (parent === document.body || parent === document.documentElement) continue;
        const clip = parent.getBoundingClientRect();
        if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) { left = Math.max(left, clip.left); right = Math.min(right, clip.right); }
        if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) { top = Math.max(top, clip.top); bottom = Math.min(bottom, clip.bottom); }
      }
      reveal(box.width > 0 && box.height > 0 && box.right > left && box.left < right && box.bottom > top && box.top < bottom);
    };
    const schedule = () => { if (!disposed && !revealed && !viewportReported && !frame) frame = requestAnimationFrame(check); };
    const observer = new IntersectionObserver(entries => {
      if (disposed || revealed) return;
      let latest: IntersectionObserverEntry | undefined;
      for (const entry of entries) if (entry.target === target && (!latest || entry.time >= latest.time)) latest = entry;
      if (latest) {
        const box = latest.boundingClientRect, actual = target?.getBoundingClientRect();
        // Legacy SVG observers can report empty bounds for a nonempty transformed viewport.
        viewportReported = !(box && (!box.width || !box.height) && actual && actual.width > 0 && actual.height > 0);
        if (viewportReported) { cancelAnimationFrame(frame); frame = 0; reveal(latest.isIntersecting); return; }
      }
      // An HTML observation supplies the fallback when SVG reports are absent or malformed.
      schedule();
    }, { rootMargin: '200px' });
    let htmlHost: Element | null = ref.current, svg: SVGSVGElement | null = ref.current?.ownerSVGElement ?? null;
    while (htmlHost instanceof SVGElement) htmlHost = htmlHost.parentElement;
    while (svg?.ownerSVGElement) svg = svg.ownerSVGElement;
    if (htmlHost) observer.observe(htmlHost);
    if (target && target !== htmlHost) observer.observe(target);
    const transformed = new MutationObserver(schedule);
    if (svg) transformed.observe(svg, { attributes: true, attributeFilter: ['viewBox', 'transform', 'width', 'height'] });
    window.addEventListener('resize', schedule); document.addEventListener('scroll', schedule, true);
    return () => { disposed = true; observer.disconnect(); transformed.disconnect(); cancelAnimationFrame(frame); window.removeEventListener('resize', schedule); document.removeEventListener('scroll', schedule, true); };
  }, [eager]);
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    void loadArtImage(href).then(ok => { if (!cancelled) { setLoaded({ url: href, ok }); if (ok && keepPrevious) setShown(href); } });
    return () => { cancelled = true; };
  }, [href, visible, keepPrevious]);
  const ready = loaded?.url === href && loaded.ok;
  const src = visible && ready ? href : keepPrevious ? shown : null;
  if (snapshot) return <image {...props} href={href} />;
  return <g ref={ref} data-art-source={src ? 'assigned' : 'none'} data-art-decoded={ready ? 'yes' : 'no'} data-art-painted={painted === src && !!src ? 'yes' : 'no'} data-art-status={!visible ? 'deferred' : loaded?.url !== href || ready && painted !== href ? 'pending' : ready ? 'ready' : 'failed'}>
    {/* Fixed geometry stays present before load and after failure. Underlying procedural face remains readable. */}
    {placeholder && <rect x={props.x} y={props.y} width={props.width} height={props.height} fill="currentColor" opacity={0.06} pointerEvents="none" />}
    {src && <image {...props} href={src} onLoad={() => setPainted(src)} onError={() => { jobs.delete(src); if (src === shown) setShown(null); setLoaded({ url: src, ok: false }); }} />}
  </g>;
}
